import { formatNumber } from "../../components/charts/formatters"
import {
  catalogTypeIdFromKey,
  dashboardPlatformKeyFromType,
  socialNetworkTypeIdToDashboardPlatformKey,
} from "./socialNetworkCatalog"

export {
  catalogTypeIdFromKey,
  dashboardPlatformKeyFromType,
  socialNetworkTypeIdToDashboardPlatformKey,
} from "./socialNetworkCatalog"

/** Public asset paths for Soho Pulse platform chips (under `public/assets/icons/social/`). */
export const PULSE_PLATFORM_ICON_PATHS = {
  instagram: "/assets/icons/social/instagram.svg",
  facebook: "/assets/icons/social/facebook.svg",
  linkedin: "/assets/icons/social/linkedin.svg",
  twitter: "/assets/icons/social/x.png",
  tiktok: "/assets/icons/social/tiktok.svg",
}

/** Soho Pulse KPI card info-tooltip copy (from design-dashboard-preview). */
export const PULSE_CARD_DEFINITIONS = {
  inbox: "Inbound messages across all connected channels.",
  engagement: "Likes, comments, shares, and saves across platforms.",
  campaign: "Posts currently live across your connected platforms.",
  growth: "Total audience and net follower growth across connected platforms.",
}

export function pulseCardTooltipText(card) {
  if (!card) return ""
  const fromCard = typeof card.definition === "string" ? card.definition.trim() : ""
  const fromCatalog = PULSE_CARD_DEFINITIONS[card.id] || ""
  return fromCard || fromCatalog || card.title || ""
}

/** Ensure every connected platform key appears in a breakdown map (values default to 0). */
export function mergePlatformBreakdownWithKeys(breakdown, platformKeys = []) {
  const next = { ...(breakdown || {}) }
  for (const key of platformKeys) {
    if (!key) continue
    if (next[key] == null) next[key] = 0
  }
  return Object.keys(next).length > 0 ? next : null
}

/** Add missing connected platforms to pulse popover rows (0-valued defaults). */
export function ensureConnectedPlatformRows(
  existingRows,
  connectedPlatformKeys,
  labelForPlatformKey,
  defaultFields = {},
  sortFn,
  socialNetworkTypes = [],
) {
  const list = [...(existingRows || [])]
  const seen = new Set(list.map((row) => row.platform))

  for (const platformKey of connectedPlatformKeys || []) {
    if (!platformKey || seen.has(platformKey)) continue
    const typeId = catalogTypeIdFromKey(socialNetworkTypes, platformKey)
    list.push({
      platform: platformKey,
      label: labelForPlatformKey(platformKey, typeId),
      icon: PULSE_PLATFORM_ICON_PATHS[platformKey],
      ...defaultFields,
    })
    seen.add(platformKey)
  }

  if (sortFn) return sortFn(list)
  return list
}

export function formatNetNewFollowers(value) {
  const n = Number(value) || 0
  if (n > 0) return `+${formatNumber(n)}`
  if (n < 0) return `-${formatNumber(Math.abs(n))}`
  return formatNumber(0)
}

/** API `followers.growth` is a ratio (0.6 → +60%). */
export function formatFollowerGrowthPercent(growth) {
  if (growth == null || Number.isNaN(Number(growth))) return "0%"
  const rounded = Math.round(Number(growth) * 1000) / 10
  if (rounded === 0) return "0%"
  const prefix = rounded > 0 ? "+" : ""
  return `${prefix}${rounded}%`
}

/** Map social network type ids to dashboard pulse platform keys (instagram, facebook, …). */
export function typeIdTotalsToPlatformBreakdown(totalsByTypeId, socialNetworkTypes = []) {
  if (!totalsByTypeId) return null

  const breakdown = {}
  const entries =
    totalsByTypeId instanceof Map
      ? totalsByTypeId.entries()
      : Object.entries(totalsByTypeId)

  for (const [id, val] of entries) {
    const n = Number(val) || 0
    if (n <= 0) continue
    const key = socialNetworkTypeIdToDashboardPlatformKey(id, socialNetworkTypes)
    if (!key) continue
    breakdown[key] = (breakdown[key] || 0) + n
  }

  return Object.keys(breakdown).length > 0 ? breakdown : null
}

export function socialPerformancePlatformsToBreakdown(
  platforms,
  metricKey,
  valueMode = "latest",
  extractMetric = () => ({ newValue: null, previousValue: null, latestValue: null }),
  socialNetworkTypes = [],
) {
  const breakdown = {}
  for (const platform of Array.isArray(platforms) ? platforms : []) {
    const typeId = Number(platform?.social_network_type_id ?? platform?.socialNetworkTypeId)
    if (!typeId) continue
    const platformKey = socialNetworkTypeIdToDashboardPlatformKey(typeId, socialNetworkTypes)
    if (!platformKey) continue

    const metric = extractMetric(platform?.metrics ?? {}, metricKey)
    let value = 0
    if (valueMode === "new") {
      value = metric.newValue ?? 0
    } else if (valueMode === "delta") {
      value = (metric.newValue ?? 0) - (metric.previousValue ?? 0)
    } else {
      value = metric.latestValue ?? 0
    }

    if (value <= 0) continue
    breakdown[platformKey] = (breakdown[platformKey] || 0) + value
  }

  return Object.keys(breakdown).length > 0 ? breakdown : null
}
